import './associate-user-card-form.scss';
import {useTranslation} from 'react-i18next';
import {OfficeContextType} from '../../contexts/office/types';
import {useContext, useEffect, useState} from 'react';
import {OfficeContext} from '../../contexts/office/context';
import {SnackBarContext} from '../../contexts/snack-bar/context';
import {SnackBarContextType} from '../../contexts/snack-bar/types';
import {UserCard} from '../../backend/interfaces/user-card';
import {useHistory} from 'react-router-dom';
import {SubmitButton, SubmitButtonState} from '../submit-button/submit-button';
import {translations} from '../../i18n/translations';
import {
  FlatSearchSelectInput,
  SearchSelectOption,
} from '../flat-search-select-input/flat-search-select-input';
import {PutUserCard} from '../../backend/requests/put-user-card';
import {UserCardsRepository} from '../../repositories/user-cards';
import {ContentPage} from '../content-page/content-page';
import {PageHeader} from '../page-header/page-header';
import {AlertModal} from '../alert-modal/alert-modal';
import {RouterPrompt} from '../router-prompt/router-prompt';
import {CaregiverMode} from '../../enums/caregiver-mode';
import {UsersRepository} from '../../repositories/users';
import {AffiliationInfo} from '../../backend/interfaces/affiliation-info';
import {Alert} from '@mui/material';
import {SelectInput} from '../select-input/select-input';
import {LoadingContent} from '../loading-content/loading-content';
import {AppContext} from '../../contexts/app/context';
import {AppContextType} from '../../contexts/app/types';
import {getBrandName} from '../../utils/functions/get-product-name';
import SaveIcon from '@mui/icons-material/Save';

// Hold variables
const BATCH_SIZE: number = 10;

/**
 * Form to associate a card to a user.
 *
 * It uses the office context to handle the form state.
 *
 * Permissions are given by props.
 *
 * This form was created taking into account that it will exist under the senior/professional caregiver tabs.
 * Later on, if it's necessary, we can extend this component to be applied to more contexts.
 */
export const AssociateUserCardForm = (props: {
  caregiverMode: CaregiverMode.Professional | CaregiverMode.SystemAdministrator;
  userId: string;
  userName: string;
  redirectUrl: string;
  entityId?: string;
}) => {
  // Get context
  const snackBarContext: SnackBarContextType = useContext(SnackBarContext);
  const officeContext: OfficeContextType = useContext(OfficeContext);
  const appContext: AppContextType = useContext(AppContext);

  // Hold state
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState<boolean>(true);
  const [submitDialogOpened, setSubmitDialogOpened] = useState<boolean>(false);
  const [dataEntered, setDataEntered] = useState<boolean>(false);
  const [userCard, setUserCard] = useState<UserCard | undefined>();
  const [userCardError, setUserCardError] = useState<string | undefined>();
  const [showUserCardError, setShowUserCardError] = useState<boolean>(false);
  const [userAffiliations, setUserAffiliations] = useState<
    | {
        affiliations: Array<AffiliationInfo>;
        totalAffiliations: number;
        selectedAffiliation?: AffiliationInfo;
      }
    | undefined
  >();
  const [isLoadingMoreAffiliations, setIsLoadingMoreAffiliations] = useState<boolean>(false);
  const [affiliationError, setAffiliationError] = useState<string | undefined>();
  const [showAffiliationError, setShowAffiliationError] = useState<boolean>(false);

  // Translations
  const {t} = useTranslation();

  // Get history
  let history = useHistory();

  // On mount
  useEffect(() => {
    // Set the submit button state
    officeContext.setAssociateUserCardFormSubmitButtonState(SubmitButtonState.Normal);

    // Clean up
    return () => {
      // Log
      console.debug('🧹 Associate user card form button state cleaned in office context.');

      // Clear button state
      officeContext.clearAssociateUserCardFormSubmitButtonState();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Get the first batch of user affiliations
  useEffect(() => {
    const getUserAffiliations = async () => {
      try {
        // Get first batch of affiliations
        const result = await UsersRepository.instance.getUserAffiliations(
          props.userId,
          0,
          BATCH_SIZE
        );

        // Set affiliations
        if (result) {
          setUserAffiliations({
            affiliations: result.data,
            selectedAffiliation: result.data[0],
            totalAffiliations: result.totalCount,
          });
        } else {
          // Error
          throw new Error('Empty user affiliations.');
        }
      } catch (error) {
        // Log
        console.debug('🔴 Could not get the user affiliations.', error);

        // Set user affiliations
        setUserAffiliations({
          affiliations: [],
          totalAffiliations: 0,
        });
      }
    };

    if (props.caregiverMode === CaregiverMode.SystemAdministrator) {
      getUserAffiliations();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.caregiverMode, props.userId]);

  // Validation
  useEffect(() => {
    // Hold validation
    let cardValid: boolean = false;
    let affiliationValid: boolean = false;

    // Card validation
    if (userCard) {
      // No errors
      if (userCardError !== undefined) {
        setUserCardError(undefined);
      }

      // Valid
      cardValid = true;
    } else {
      // Errors
      if (showUserCardError) {
        setUserCardError(
          t(translations.associate_user_card_form.errors.form_validation_invalid_field)
        );
      }

      // Invalid
      cardValid = false;
    }

    // Affiliation validation
    if (
      userAffiliations?.selectedAffiliation ||
      props.caregiverMode === CaregiverMode.Professional
    ) {
      // No errors
      if (affiliationError !== undefined) {
        setAffiliationError(undefined);
      }

      // Valid
      affiliationValid = true;
    } else {
      // Errors
      if (showAffiliationError) {
        setAffiliationError(
          t(translations.associate_user_card_form.errors.form_validation_invalid_field)
        );
      }

      // Invalid
      affiliationValid = false;
    }

    // Data was entered
    if (
      userCard ||
      (userAffiliations?.selectedAffiliation &&
        userAffiliations.selectedAffiliation.id !== userAffiliations.affiliations[0].id)
    ) {
      setDataEntered(true);
    } else {
      setDataEntered(false);
    }

    // Submit button validation
    if (cardValid && affiliationValid) {
      // Enable submit button
      setSubmitButtonDisabled(false);
    } else {
      // Disable submit button
      setSubmitButtonDisabled(true);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    showUserCardError,
    userCard,
    userCardError,
    showAffiliationError,
    userAffiliations,
    affiliationError,
    props.caregiverMode,
  ]);

  // Save changes
  const handleSaveChanges = (event: any) => {
    event.preventDefault();

    // Open the confirmation modal
    setSubmitDialogOpened(true);
  };

  // Handle the confirmation modal opened state
  const handleSubmitDialogOpened = (opened: boolean) => {
    // Change state
    setSubmitDialogOpened(opened);
  };

  // Submit
  const handleSubmit = async () => {
    try {
      // Close confirm dialog
      setSubmitDialogOpened(false);

      // Update submit button state
      officeContext.setAssociateUserCardFormSubmitButtonState(SubmitButtonState.Loading);

      // Verify but it's not expected
      if (!userCard || !userCard.id) {
        // Throw error
        throw new Error('Empty user card.');
      }

      // Request body
      const requestBody: PutUserCard = {
        isActive: userCard.isActive,
        userId: props.userId,
      };

      // Associate user card
      await UserCardsRepository.instance.updateById(userCard.id, requestBody);

      // Show toast
      snackBarContext.setOfficeToastNotification({
        severity: 'success',
        message: t(translations.associate_user_card_form.success.success_message, {
          name: props.userName,
        }),
      });

      // Wait some time just to make sure the button state was updated
      // so the router prompt won't appear when the history is updated.
      setTimeout(() => {
        // Redirect
        history.push(props.redirectUrl);
      }, 500);
    } catch (error) {
      // Log
      console.debug('🔴 Could not associate the user card.', error);

      // Update submit button state
      officeContext.setAssociateUserCardFormSubmitButtonState(SubmitButtonState.Error);
    }
  };

  // Handle fetch user cards
  const handleFetchUserCards = async (
    limit: number,
    skip: number,
    search?: string
  ): Promise<{data: Array<SearchSelectOption<UserCard>>; total: number}> => {
    if (props.caregiverMode === CaregiverMode.Professional) {
      // Check entity id
      if (!props.entityId) {
        // Error
        throw new Error('Empty entity id.');
      }

      // Get available user cards
      const response = await UserCardsRepository.instance.getInEntityAsAffiliatedForEntity(
        props.entityId,
        limit,
        skip,
        true,
        search
      );

      // Return
      return {
        data: response.data.map((obj) => {
          return {id: obj.id || '', name: obj.name || '', item: obj};
        }),
        total: response.totalCount,
      };
    } else if (
      props.caregiverMode === CaregiverMode.SystemAdministrator &&
      userAffiliations?.selectedAffiliation
    ) {
      // Get available user cards
      const response = await UserCardsRepository.instance.getAsSystemAdministrator(
        limit,
        skip,
        userAffiliations.selectedAffiliation.entity.id,
        undefined,
        true,
        search
      );

      // Return
      return {
        data: response.data.map((obj) => {
          return {id: obj.id || '', name: obj.name || '', item: obj};
        }),
        total: response.totalCount,
      };
    } else {
      // Log
      console.debug('ℹ️ Missing permissions.');

      // Return
      return {
        data: [],
        total: 0,
      };
    }
  };

  // Handle change the user card
  const handleChangeUserCard = (value?: SearchSelectOption<UserCard>) => {
    setUserCard(value?.item || undefined);
  };

  // Handle blur the user card input
  const handleBlurUserCard = () => {
    if (!showUserCardError) {
      setShowUserCardError(true);
    }
  };

  // Handle blue of the affiliation input
  const handleBlurAffiliation = () => {
    if (!showAffiliationError) {
      setShowAffiliationError(true);
    }
  };

  // Handle change affiliation
  const handleChangeAffiliation = (value: string) => {
    if (userAffiliations?.selectedAffiliation?.id !== value) {
      // Search for the newly selected affiliation
      const selectedAffiliation: AffiliationInfo | undefined = userAffiliations?.affiliations.find(
        (obj) => obj.id === value
      );

      if (userAffiliations && selectedAffiliation) {
        // Update the newly selected affiliation
        setUserAffiliations({
          selectedAffiliation,
          affiliations: userAffiliations.affiliations,
          totalAffiliations: userAffiliations.totalAffiliations,
        });

        // Reset user card
        setUserCard(undefined);
      }
    }
  };

  // Fetch more affiliations
  const handleFetchMoreAffiliations = async () => {
    try {
      // Start loading
      setIsLoadingMoreAffiliations(true);

      // Get more affiliations
      const result = await UsersRepository.instance.getUserAffiliations(
        props.userId,
        userAffiliations?.affiliations.length || 0,
        BATCH_SIZE
      );

      // Set affiliations
      if (result) {
        // New affiliations
        const newAffiliations: Array<AffiliationInfo> = userAffiliations?.affiliations
          ? [...userAffiliations.affiliations]
          : [];

        for (let i = 0; i < result.data.length; i++) {
          if (!newAffiliations.find((obj) => obj.id === result.data[i].id)) {
            newAffiliations.push(result.data[i]);
          }
        }

        // Set state
        setUserAffiliations({
          affiliations: newAffiliations,
          totalAffiliations: result.totalCount,
          selectedAffiliation: userAffiliations?.selectedAffiliation || newAffiliations[0],
        });
      } else {
        // Error
        throw new Error('Empty user affiliations.');
      }
    } catch (error) {
      // Log
      console.debug('🔴 Could not fetch more user affiliations.', error);
    } finally {
      // Stop loading
      setIsLoadingMoreAffiliations(false);
    }
  };

  return (
    <ContentPage>
      {props.caregiverMode === CaregiverMode.SystemAdministrator && !userAffiliations && (
        <LoadingContent marginTop="32px" />
      )}
      {((props.caregiverMode === CaregiverMode.Professional && !props.entityId) ||
        (props.caregiverMode === CaregiverMode.SystemAdministrator &&
          userAffiliations &&
          userAffiliations.affiliations.length === 0)) && (
        <Alert severity="info" className="associate-user-card-form-info-message">
          {t(translations.associate_user_card_form.no_affiliations_info_message)}
        </Alert>
      )}
      {((props.caregiverMode === CaregiverMode.Professional && props.entityId) ||
        (props.caregiverMode === CaregiverMode.SystemAdministrator &&
          userAffiliations &&
          userAffiliations.affiliations.length > 0 &&
          userAffiliations.selectedAffiliation)) && (
        <>
          <form onSubmit={handleSaveChanges}>
            <PageHeader
              title={t(translations.associate_user_card_form.title)}
              isSmallTitle={true}
              rightButtons={
                <SubmitButton
                  disabled={submitButtonDisabled}
                  state={
                    officeContext.state.associateUserCardFormSubmitButtonState ||
                    SubmitButtonState.Normal
                  }
                  onStateChange={(newState: SubmitButtonState) => {
                    officeContext.setAssociateUserCardFormSubmitButtonState(newState);
                  }}
                  normalStateLabel={t(translations.associate_user_card_form.save_button)}
                  loadingStateLabel={t(translations.associate_user_card_form.saving_button)}
                  successStateLabel={t(
                    translations.associate_user_card_form.success.save_success_button
                  )}
                  errorStateLabel={t(
                    translations.associate_user_card_form.errors.error_on_save_button
                  )}
                  endIcon={<SaveIcon />}
                  type="submit"
                />
              }
            />
            {props.caregiverMode === CaregiverMode.SystemAdministrator && userAffiliations && (
              <SelectInput
                label={t(translations.associate_user_card_form.entity_input)}
                items={userAffiliations.affiliations.map((obj) => {
                  return {
                    value: obj.id,
                    label: obj.entity.name,
                    avatar: obj.entity.picture,
                  };
                })}
                totalItems={userAffiliations.totalAffiliations}
                value={userAffiliations.selectedAffiliation?.id}
                avatar={{
                  type: 'entity',
                  isIcon: false,
                }}
                handleChange={handleChangeAffiliation}
                handleFetchMore={handleFetchMoreAffiliations}
                loadingMore={isLoadingMoreAffiliations}
                disabled={
                  officeContext.state.associateUserCardFormSubmitButtonState ===
                  SubmitButtonState.Loading
                }
                required={true}
                error={affiliationError}
                handleBlur={handleBlurAffiliation}
              />
            )}
            <FlatSearchSelectInput<UserCard>
              className={
                props.caregiverMode === CaregiverMode.SystemAdministrator
                  ? 'associate-user-card-form-field'
                  : ''
              }
              label={t(translations.associate_user_card_form.card_name_input)}
              value={
                userCard
                  ? {
                      id: userCard.id || '',
                      name: userCard.name || '',
                      item: userCard,
                    }
                  : undefined
              }
              error={userCardError}
              disabled={
                officeContext.state.associateUserCardFormSubmitButtonState ===
                SubmitButtonState.Loading
              }
              required={true}
              disableClear={true}
              fetchWhenEmpty={true}
              handleFetchData={handleFetchUserCards}
              handleChange={handleChangeUserCard}
              handleBlur={handleBlurUserCard}
            />
            {props.caregiverMode === CaregiverMode.SystemAdministrator && (
              <Alert severity="info" className="associate-user-card-form-alert">
                {t(translations.associate_user_card_form.card_name_info_message)}
              </Alert>
            )}
          </form>
          <AlertModal
            id="confirm-associate-user-card"
            opened={submitDialogOpened}
            title={t(translations.associate_user_card_form.save_dialog_title, {
              name: props.userName,
              card: userCard?.name || '',
            })}
            content={t(translations.associate_user_card_form.save_dialog_message, {
              name: props.userName,
              brand: getBrandName(appContext.state.configuration.product),
            })}
            handleClose={() => handleSubmitDialogOpened(false)}
            actions={[
              {
                label: t(translations.associate_user_card_form.save_dialog_action_cancel_button),
                type: 'neutral',
                handleClick: () => handleSubmitDialogOpened(false),
              },
              {
                label: t(translations.associate_user_card_form.save_dialog_action_save_button),
                type: 'nondestructive',
                handleClick: handleSubmit,
              },
            ]}
          />
          <RouterPrompt
            when={
              dataEntered &&
              officeContext.state.associateUserCardFormSubmitButtonState !==
                SubmitButtonState.Loading
            }
            subtitle={t(translations.associate_user_card_form.router_prompt_title)}
            message={t(translations.associate_user_card_form.router_prompt_message)}
            onOK={() => true}
            onCancel={() => false}
            toastNotification={{
              severity: 'info',
              message: t(translations.associate_user_card_form.cancel_toast),
            }}
          />
        </>
      )}
    </ContentPage>
  );
};
